import { IDentalChart } from '@dataTypes/chartingFeature.types'
import { ITreatmentPlan, TX_PLAN_STATUS } from '@dataTypes/treatmentPlan.types'
import { ProcedureSettingsType } from '@dataTypes/procedureSettings.type'
import moment from 'moment'

export const mockDentalCharts: Record<string, IDentalChart[]> = {
  'mock-patient-1': [
    {
      chart_id: 'mock-chart-1-1',
      patient_id: 'mock-patient-1',
      provider_id: 'provider-1',
      clinic_id: 'clinic-1',
      title: 'Initial Examination',
      description: 'Initial dental examination and charting',
      dental_data: [
        {
          tooth_number: '18',
          condition: 'missing',
          type: null,
          material: null,
          surface: null,
          recurrent: false,
        },
        {
          tooth_number: '17',
          condition: 'missing',
          type: null,
          material: null,
          surface: null,
          recurrent: false,
        },
        {
          tooth_number: '32',
          condition: 'missing',
          type: null,
          material: null,
          surface: null,
          recurrent: false,
        },
        {
          tooth_number: '31',
          condition: 'missing',
          type: null,
          material: null,
          surface: null,
          recurrent: false,
        },
      ],
      date_time_created_at: moment().subtract(6, 'months').toISOString(),
      date_time_updated_at: moment().subtract(6, 'months').toISOString(),
    },
  ],
  'mock-patient-2': [
    {
      chart_id: 'mock-chart-2-1',
      patient_id: 'mock-patient-2',
      provider_id: 'provider-1',
      clinic_id: 'clinic-1',
      title: 'Routine Checkup',
      description: 'Regular dental checkup',
      dental_data: [
        {
          tooth_number: '16',
          condition: 'missing',
          type: null,
          material: null,
          surface: null,
          recurrent: false,
        },
        {
          tooth_number: '1',
          condition: 'missing',
          type: null,
          material: null,
          surface: null,
          recurrent: false,
        },
      ],
      date_time_created_at: moment().subtract(3, 'months').toISOString(),
      date_time_updated_at: moment().subtract(3, 'months').toISOString(),
    },
  ],
}

const createProcedureSettings = (
  procedure_id: string,
  procedure_code: string,
  description: string,
): ProcedureSettingsType => ({
  procedure_setting_id: `mock-proc-setting-${procedure_id}`,
  appointment_type_subcategory_id: 'subcat-1',
  procedure_id,
  description,
  fee1: 150,
  fee2: 200,
  fee3: 250,
  date_time_created_at: moment().subtract(1, 'month').toISOString(),
  date_time_updated_at: moment().subtract(1, 'month').toISOString(),
  procedure: {
    procedure_id,
    procedure_code,
    description,
  },
})

export const mockTreatmentPlans: Record<string, ITreatmentPlan[]> = {
  'mock-patient-1': [
    {
      tp_id: 'mock-tp-1-1',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      insurance_plan_id: 'ins-1',
      tp_heading: 'Comprehensive Treatment Plan 2024',
      tp_note: 'Complete oral rehabilitation plan',
      tp_type: 'Comprehensive',
      isDraft: false,
      discount_id: null,
      discount: 0,
      date_time_created_at: moment().subtract(2, 'months').toISOString(),
      date_time_updated_at: moment().subtract(1, 'month').toISOString(),
      phases: [
        {
          phase_id: 'mock-phase-1-1-1',
          phase_name: 'Phase 1: Preventive Care',
          scheduled_date: moment().add(1, 'week').format('YYYY-MM-DD'),
          status: TX_PLAN_STATUS.ACCEPTED,
          items: [
            {
              item_id: 'mock-item-1-1-1-1',
              phase_id: 'mock-phase-1-1-1',
              procedure_id: 'proc-1',
              surface: '14/O, 15/O, 16/O',
              charges: 450,
              insurance: 360,
              adjustments: 0,
              outstanding: 90,
              procedure_settings: createProcedureSettings(
                'proc-1',
                'D1110',
                'Adult prophylaxis',
              ),
            },
            {
              item_id: 'mock-item-1-1-1-2',
              phase_id: 'mock-phase-1-1-1',
              procedure_id: 'proc-2',
              surface: '24/O, 25/O, 26/O',
              charges: 450,
              insurance: 360,
              adjustments: 0,
              outstanding: 90,
              procedure_settings: createProcedureSettings(
                'proc-2',
                'D1110',
                'Adult prophylaxis',
              ),
            },
          ],
        },
        {
          phase_id: 'mock-phase-1-1-2',
          phase_name: 'Phase 2: Restorative Treatment',
          scheduled_date: null,
          status: TX_PLAN_STATUS.PROPOSED,
          items: [
            {
              item_id: 'mock-item-1-1-2-1',
              phase_id: 'mock-phase-1-1-2',
              procedure_id: 'proc-3',
              surface: '3/MO',
              charges: 350,
              insurance: 280,
              adjustments: 0,
              outstanding: 70,
              procedure_settings: createProcedureSettings(
                'proc-3',
                'D2150',
                'Amalgam restoration - two surfaces',
              ),
            },
            {
              item_id: 'mock-item-1-1-2-2',
              phase_id: 'mock-phase-1-1-2',
              procedure_id: 'proc-4',
              surface: '19/DO',
              charges: 300,
              insurance: 240,
              adjustments: 0,
              outstanding: 60,
              procedure_settings: createProcedureSettings(
                'proc-4',
                'D2140',
                'Amalgam restoration - one surface',
              ),
            },
          ],
        },
        {
          phase_id: 'mock-phase-1-1-3',
          phase_name: 'Phase 3: Completed Procedures',
          scheduled_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
          status: TX_PLAN_STATUS.COMPLETED,
          items: [
            {
              item_id: 'mock-item-1-1-3-1',
              phase_id: 'mock-phase-1-1-3',
              procedure_id: 'proc-5',
              surface: '8/F',
              charges: 200,
              insurance: 160,
              adjustments: 0,
              outstanding: 40,
              procedure_settings: createProcedureSettings(
                'proc-5',
                'D2391',
                'Resin-based composite - one surface, anterior',
              ),
            },
          ],
        },
      ],
    },
    {
      tp_id: 'mock-tp-1-2',
      patient_id: 'mock-patient-1',
      clinic_id: 'clinic-1',
      insurance_plan_id: 'ins-1',
      tp_heading: 'Orthodontic Consultation',
      tp_note: 'Evaluation for orthodontic treatment',
      tp_type: 'Limited',
      isDraft: false,
      discount_id: null,
      discount: 0,
      date_time_created_at: moment().subtract(1, 'week').toISOString(),
      date_time_updated_at: moment().subtract(1, 'week').toISOString(),
      phases: [
        {
          phase_id: 'mock-phase-1-2-1',
          phase_name: 'Initial Consultation',
          scheduled_date: null,
          status: TX_PLAN_STATUS.PROPOSED,
          items: [
            {
              item_id: 'mock-item-1-2-1-1',
              phase_id: 'mock-phase-1-2-1',
              procedure_id: 'proc-6',
              surface: null,
              charges: 150,
              insurance: 120,
              adjustments: 0,
              outstanding: 30,
              procedure_settings: createProcedureSettings(
                'proc-6',
                'D0150',
                'Comprehensive oral evaluation',
              ),
            },
          ],
        },
      ],
    },
  ],
  'mock-patient-2': [
    {
      tp_id: 'mock-tp-2-1',
      patient_id: 'mock-patient-2',
      clinic_id: 'clinic-1',
      insurance_plan_id: 'ins-2',
      tp_heading: 'Periodontal Treatment Plan',
      tp_note: 'Treatment for gum disease',
      tp_type: 'Comprehensive',
      isDraft: false,
      discount_id: null,
      discount: 0,
      date_time_created_at: moment().subtract(1, 'month').toISOString(),
      date_time_updated_at: moment().subtract(2, 'weeks').toISOString(),
      phases: [
        {
          phase_id: 'mock-phase-2-1-1',
          phase_name: 'Phase 1: Scaling and Root Planing',
          scheduled_date: moment().add(2, 'weeks').format('YYYY-MM-DD'),
          status: TX_PLAN_STATUS.ACCEPTED,
          items: [
            {
              item_id: 'mock-item-2-1-1-1',
              phase_id: 'mock-phase-2-1-1',
              procedure_id: 'proc-7',
              surface: '2,3,4,5,6,7,8,9,10,11,12,13,14,15',
              charges: 800,
              insurance: 640,
              adjustments: 0,
              outstanding: 160,
              procedure_settings: createProcedureSettings(
                'proc-7',
                'D4341',
                'Periodontal scaling and root planing - four or more teeth per quadrant',
              ),
            },
            {
              item_id: 'mock-item-2-1-1-2',
              phase_id: 'mock-phase-2-1-1',
              procedure_id: 'proc-8',
              surface: '18,19,20,21,22,23,24,25,26,27,28,29,30,31',
              charges: 800,
              insurance: 640,
              adjustments: 0,
              outstanding: 160,
              procedure_settings: createProcedureSettings(
                'proc-8',
                'D4341',
                'Periodontal scaling and root planing - four or more teeth per quadrant',
              ),
            },
          ],
        },
        {
          phase_id: 'mock-phase-2-1-2',
          phase_name: 'Phase 2: Maintenance',
          scheduled_date: null,
          status: TX_PLAN_STATUS.PROPOSED,
          items: [
            {
              item_id: 'mock-item-2-1-2-1',
              phase_id: 'mock-phase-2-1-2',
              procedure_id: 'proc-9',
              surface: null,
              charges: 200,
              insurance: 160,
              adjustments: 0,
              outstanding: 40,
              procedure_settings: createProcedureSettings(
                'proc-9',
                'D4910',
                'Periodontal maintenance',
              ),
            },
          ],
        },
      ],
    },
  ],
}
